import React from "react";
import type { IQuestion } from "@/apis/exam_papers/types";

interface PrintableQuestionsViewProps {
  questions: (IQuestion & { 
    originalIndex?: number;
    moduleIndex?: number;
    moduleQuestionIndex?: number;
  })[];
  title?: string;
  examType?: string;
  subject?: string;
}

const PrintableQuestionsView: React.FC<PrintableQuestionsViewProps> = ({
  questions,
  title,
  examType,
  subject,
}) => {
  // Get choice letter (A, B, C, D)
  const getChoiceLetter = (idx: number) => String.fromCharCode(65 + idx);

  // Get question number display
  const getQuestionNumber = (question: any, questionIndex: number) => {
    if (question.moduleIndex && question.moduleQuestionIndex) {
      return `Module ${question.moduleIndex} - Q${question.moduleQuestionIndex}`;
    }
    return `Question ${question.originalIndex || questionIndex + 1}`;
  };

  return (
    <div className="w-full max-w-none mx-auto bg-white">
      {/* Print Styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media print {
          body {
            margin: 0;
            padding: 0;
            font-size: 12pt;
            line-height: 1.4;
          }

          .print-container {
            width: 100%;
            max-width: none;
            margin: 0;
            padding: 0.5in;
            box-shadow: none;
            min-width: 100%;
          }

          .print-title {
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 20pt;
            text-align: center;
            break-after: avoid;
          }

          .question-block {
            break-inside: avoid;
            padding: 12pt 0;
            border: none;
            box-shadow: none;
            min-height: calc((11in - 1in) / 2 - 40pt);
            max-height: calc(11in - 1in - 40pt);
            width: 100%;
          }

          .question-block:not(:last-child) {
            margin-bottom: 24pt;
          }

          .question-number {
            font-size: 14pt;
            font-weight: bold;
            margin-bottom: 12pt;
          }

          .two-column-layout {
            display: flex;
            gap: 24pt;
            align-items: flex-start;
          }

          .left-column {
            flex: 1;
            min-width: 0;
          }

          .right-column {
            flex: 1;
            min-width: 0;
          }

          .passage-section {
            margin-bottom: 16pt;
          }

          .question-section {
            margin-top: 0;
            width: 100%;
          }

          .choice-item {
            margin-bottom: 8pt;
            break-inside: avoid;
            display: flex;
            align-items: flex-start;
          }

          .choice-letter {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 24pt;
            height: 24pt;
            border: 1pt solid #000;
            border-radius: 50%;
            margin-right: 8pt;
            font-weight: bold;
            flex-shrink: 0;
          }

          .choice-text {
            flex: 1;
            line-height: 1.4;
          }
        }

        @media screen {
          .print-container {
            width: 8.5in;
            margin: 20px auto;
            padding: 0.5in;
            background: white;
          }

          .question-block {
            padding: 30px;
            margin-bottom: 40px;
            border: 2px dashed #ccc;
            box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
            background: white;
            min-height: calc((11in - 1in) / 2 - 60px);
            max-height: calc(11in - 1in - 60px);
            display: flex;
            flex-direction: column;
          }

          .question-block:last-child {
            margin-bottom: 0;
          }

          .two-column-layout {
            display: flex;
            gap: 24px;
            align-items: flex-start;
            flex: 1;
          }

          .left-column {
            flex: 1;
            min-width: 0;
          }

          .right-column {
            flex: 1;
            min-width: 0;
          }

          .single-column-layout {
            flex: 1;
            display: flex;
            flex-direction: column;
            width: 100%;
          }
        }
        `,
        }}
      />

      <div className="print-container">
        {/* Title */}
        {title && (
          <div className="print-title text-center text-xl font-bold mb-6">
            {title}
          </div>
        )}

        {/* Questions */}
        {questions.map((question, questionIndex) => {
          // Question content component (reusable)
          const questionContent = (
            <div className="question-section">
              {/* Question Number */}
              <div className="question-number text-lg font-bold mb-4">
                {getQuestionNumber(question, questionIndex)}
              </div>

              {/* Question Text */}
              <div className="mb-6">
                <div
                  className="text-base leading-relaxed font-serif"
                  style={{ fontFamily: '"Times New Roman", Times, serif' }}
                  dangerouslySetInnerHTML={{ __html: question.question }}
                />
              </div>

              {/* Answer Section */}
              {question.answer_type === "short_answer" ? (
                <div className="mb-6">
                  <div className="text-base font-medium mb-2">Answer:</div>
                  <div className="border-b-2 border-gray-400 w-full max-w-xs h-8"></div>
                </div>
              ) : (
                /* Multiple Choice Options */
                <div className="space-y-3">
                  {question.choices.map((choice, idx) => {
                    const choiceLetter = getChoiceLetter(idx);

                    return (
                      <div
                        key={choice.id}
                        className="choice-item flex items-start"
                      >
                        <span className="choice-letter inline-flex items-center justify-center w-6 h-6 rounded-full border border-black text-sm font-bold mr-3 flex-shrink-0">
                          {choiceLetter}
                        </span>
                        <div
                          className="choice-text flex-1 text-base leading-relaxed font-serif"
                          style={{
                            fontFamily: '"Times New Roman", Times, serif',
                          }}
                          dangerouslySetInnerHTML={{ __html: choice.text }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          // Check if should show passage (not for SAT Math)
          const shouldShowPassage =
            question.passage && !(examType === "SAT" && subject === "math");

          return (
            <div key={question.id} className="question-block">
              {shouldShowPassage ? (
                /* Two-column layout for questions with passage */
                <div className="two-column-layout">
                  {/* Left Column - Passage */}
                  <div className="left-column">
                    <div className="passage-section">
                      <div
                        className="text-base leading-relaxed font-serif"
                        style={{
                          fontFamily: '"Times New Roman", Times, serif',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: question.passage || "",
                        }}
                      />
                    </div>
                  </div>

                  {/* Right Column - Question Content */}
                  <div className="right-column">{questionContent}</div>
                </div>
              ) : (
                /* Single-column layout for questions without passage */
                <div className="single-column-layout w-full">
                  {questionContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PrintableQuestionsView;
